import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { useEffect } from 'react';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Button, Card, Container, Stack } from '@optimus-web/core';
import { createOption, OverlayedLoader } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import {
    AccountingConfigurationsAccountingCategories,
    AccountingEnvironment,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { getAccountingEnvironmentByCode } from '@mews-commander/legacy-entities/src/ducks/accountingEnvironments';
import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { AdditionalExpenses } from './formSections/AdditionalExpenses';
import { PaymentCategories } from './formSections/PaymentCategories';
import { AlternativePayments } from './formSections/AlternativePayments';
import { PaymentMethods } from './formSections/PaymentMethods';
import { ExternalPayments } from './formSections/ExternalPayments';

export const AccountingCategoriesPage = () => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data: accountingCategories = [], isLoading: isLoadingAccountingCategories } = useRepositoryQuery(
        'accountingCategories',
        'getAll',
        {
            enterpriseId,
        },
        {
            select: (data) => data.accountingCategories?.filter((it) => it.isActive),
        }
    );
    const { data: originalData, isLoading: isLoadingAccountingConfiguration } = useRepositoryQuery(
        'accountingConfigurations',
        'get',
        {
            enterpriseId,
        }
    );
    const { mutateAsync: mutateAccountingConfiguration } = useRepositoryMutation(
        'accountingConfigurations',
        'updateAccountingCategoriesSettings',
        {
            invalidateQueries: { accountingConfigurations: ['get'] },
        }
    );

    const { accountingEnvironmentCode } = useEnterprise(enterpriseId);
    const accountingEnvironment = useCommanderSelector((state) =>
        getAccountingEnvironmentByCode(state, { code: accountingEnvironmentCode })
    ) as AccountingEnvironment;

    const onSubmit = async (values: AccountingConfigurationsAccountingCategories) => {
        await mutateAccountingConfiguration({
            ...values,
            enterpriseId,
        });
        showSuccessAlert({ description: lt('SaveSuccessMessage') });
    };

    const { renderFormContent, reset, isSubmitting } = useFormControls<AccountingConfigurationsAccountingCategories>({
        onSubmit,
        mode: 'onChange',
        defaultValues: {},
    });

    const isLoading = isLoadingAccountingCategories || isLoadingAccountingConfiguration || isSubmitting;

    useEffect(() => {
        if (originalData?.accountingCategories) reset(originalData.accountingCategories);
    }, [originalData, reset]);

    const areExternalPaymentsEnabled = pipe(
        accountingEnvironment.allowedExternalPaymentTypes,
        option.fromNullable,
        option.map((it) => it.length),
        option.fold(
            () => false,
            (value) => value > 0
        )
    );

    const options = pipe(
        accountingCategories,
        array.map((it) =>
            createOption({
                label: it.name,
                value: it.id,
            })
        )
    );

    return (
        <Container align="center" maxWidth="640px">
            <Card padding="200">
                <Stack vertical>
                    <OverlayedLoader fixed show={isLoading} />

                    {renderFormContent(
                        <Stack vertical spacing="200">
                            <PaymentCategories accountingEnvironment={accountingEnvironment} options={options} />
                            <AdditionalExpenses accountingEnvironment={accountingEnvironment} options={options} />
                            {originalData?.accountingCategories?.terminalPayments ? (
                                <PaymentMethods
                                    prefix="terminalPayments"
                                    options={options}
                                    titleTextKey={TextKey.TerminalPaymentPlural}
                                />
                            ) : null}
                            <PaymentMethods
                                prefix="gatewayPayments"
                                options={options}
                                titleTextKey={TextKey.GatewayPaymentPlural}
                            />
                            <PaymentMethods
                                prefix="mewsTerminalPayments"
                                options={options}
                                titleTextKey={TextKey.MewsTerminalPaymentPlural}
                            />
                            {areExternalPaymentsEnabled ? (
                                <ExternalPayments accountingEnvironment={accountingEnvironment} options={options} />
                            ) : null}
                            <AlternativePayments accountingEnvironment={accountingEnvironment} options={options} />

                            <Stack alignment="end">
                                <Button type="submit" variant="primary">
                                    <LocalizedText textKey={TextKey.Save} />
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Card>
        </Container>
    );
};
